import { SITE_BASE_URL, ORG, buildDomainFaqs } from "./agenticSeoUtils";

const img = (file) => `${process.env.PUBLIC_URL || ""}/use-cases/${file}`;

/** Resolve relative SPA asset paths to absolute crawlable URLs. */
export function absoluteAssetUrl(path) {
  if (!path) return `${SITE_BASE_URL}/logo.png`;
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith("/build/")) return `${SITE_BASE_URL}${path.replace(/^\/build/, "")}`;
  if (path.startsWith("/")) return `${SITE_BASE_URL}${path}`;
  return `${SITE_BASE_URL}/${path}`;
}

export const FINANCIAL_DOMAIN_INTRO = {
  title: "Financial Domain",
  content:
    "Agentic AI + Workflow Automation across finance operations — Accounts Payable, Procure to Pay, Accounts Receivable, Order to Cash, Treasury, Tax, FP&A, General Ledger Close, Expense Management, Audit, Compliance, and Shared Services. Practical solutions for enterprises in India, UAE, KSA, UK, and USA.",
  seoDescription:
    "Financial Domain agentic AI use cases — invoice matching, cash application, sales order validation, tax validation, journal entry checks, expense policy, audit evidence, and finance shared services automation.",
};

const SUB_INDUSTRY_IMAGES = {
  "Accounts Payable": {
    file: "Accounts_Payable.png",
    alt: "AI-Powered Accounts Payable Automation process flow",
  },
  "Accounts Receivable": {
    file: "Accounts_Receivable.png",
    alt: "AI-Powered Accounts Receivable Automation process flow",
  },
  "Procure to Pay": {
    file: "Procure_to_Pay.png",
    alt: "AI-Powered Procure-to-Pay Automation process flow",
  },
  "Order to Cash": {
    file: "Order_to_Cash.png",
    alt: "AI-Powered Order-to-Cash Automation process flow",
  },
  Treasury: {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  "Internal Audit": {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  Tax: {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  "FP&A": {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  "General Ledger Close": {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  "Expense Management": {
    file: "Expense_Management.png",
    alt: "AI-Powered Expense Management Automation process flow",
  },
  "Fixed Assets": {
    file: "Procure_to_Pay.png",
    alt: "AI-Powered Procure-to-Pay Automation process flow",
  },
  "Procurement Finance": {
    file: "Accounts_Payable.png",
    alt: "AI-Powered Accounts Payable Automation process flow",
  },
  "Finance Compliance Reporting": {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  "Finance Shared Services": {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
  "Payroll Finance": {
    file: "Record_to_Report.png",
    alt: "AI-Powered Record-to-Report Automation process flow",
  },
};

const slug = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function withImage(uc) {
  const visual =
    SUB_INDUSTRY_IMAGES[uc.subIndustry] || SUB_INDUSTRY_IMAGES["Accounts Payable"];
  return {
    ...uc,
    groupId: slug(uc.subIndustry),
    image: img(visual.file),
    imageAlt: visual.alt,
  };
}

const RAW = [
  // Accounts Payable
  {
    id: "po-grn-invoice-three-way-matching",
    subIndustry: "Accounts Payable",
    title: "PO-GRN-Invoice Three-Way Matching",
    seoKeywords:
      "po-grn-invoice three-way matching automation, accounts payable AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Accounts Payable teams often manage po-grn-invoice three-way matching for Accounts Payable through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into manual matching between invoice, purchase order and receipt.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to compare documents, identify mismatches and prepare exception notes. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "vendor-query-email-triage",
    subIndustry: "Accounts Payable",
    title: "Vendor Query Email Triage",
    seoKeywords:
      "vendor query email triage automation, accounts payable AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Accounts Payable teams often manage vendor query email triage for Accounts Payable through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into repeated vendor questions on invoice and payment status.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to classify vendor emails, fetch invoice status and draft replies. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Procure to Pay
  {
    id: "quotation-comparison-recommendation",
    subIndustry: "Procure to Pay",
    title: "Quotation Comparison and Recommendation",
    seoKeywords:
      "quotation comparison and recommendation automation, procure to pay AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Procure to Pay teams often manage quotation comparison and recommendation through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into manual comparison of multiple supplier quotes.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to read quotations, compare price, terms and delivery and prepare recommendation summary. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "contract-compliance-check-purchases",
    subIndustry: "Procure to Pay",
    title: "Contract Compliance Check for Purchases",
    seoKeywords:
      "contract compliance check for purchases automation, procure to pay AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Procure to Pay teams often manage contract compliance check for purchases through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into purchases made outside negotiated contract terms.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to compare PR or PO terms with contract clauses and flag gaps. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Accounts Receivable
  {
    id: "cash-application-remittance-matching",
    subIndustry: "Accounts Receivable",
    title: "Cash Application and Remittance Matching",
    seoKeywords:
      "cash application and remittance matching automation, accounts receivable AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Accounts Receivable teams often manage cash application and remittance matching for Accounts Receivable through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into unmatched receipts and delayed customer ledger updates.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to match bank receipts with invoices and post or queue exceptions. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "invoice-dispute-triage",
    subIndustry: "Accounts Receivable",
    title: "Invoice Dispute Triage",
    seoKeywords:
      "invoice dispute triage automation, accounts receivable AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Accounts Receivable teams often manage invoice dispute triage for Accounts Receivable through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into unstructured dispute emails and slow resolution.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to classify disputes, fetch supporting data and route to owners. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "credit-note-validation",
    subIndustry: "Accounts Receivable",
    title: "Credit Note Validation",
    seoKeywords:
      "credit note validation automation, accounts receivable AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Accounts Receivable teams often manage credit note validation for Accounts Receivable through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into return, discount or billing error reasons before credit approval.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate return, discount or billing error reasons before credit approval. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Order to Cash
  {
    id: "sales-order-validation",
    subIndustry: "Order to Cash",
    title: "Sales Order Validation",
    seoKeywords:
      "sales order validation automation, order to cash AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Order to Cash teams often manage sales order validation through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into orders entered with missing price, tax, or customer data.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate order fields, pricing and credit status before fulfillment. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Treasury
  {
    id: "payment-release-control",
    subIndustry: "Treasury",
    title: "Payment Release Control",
    seoKeywords:
      "payment release control automation, treasury AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Treasury teams often manage payment release control through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into payment files released without structured exception review.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate payment batch, sanctions flags and approval evidence before release. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Tax
  {
    id: "tds-withholding-tax-validation",
    subIndustry: "Tax",
    title: "TDS / Withholding Tax Validation",
    seoKeywords:
      "tds withholding tax validation automation, tax AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Tax teams often manage tds / withholding tax validation for Tax through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into wrong tax deduction codes and manual checks.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate vendor, section, rate and invoice values before posting. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // FP&A
  {
    id: "budget-variance-commentary-drafting",
    subIndustry: "FP&A",
    title: "Budget Variance Commentary Drafting",
    seoKeywords:
      "budget variance commentary drafting automation, fp&a AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "FP&A teams often manage budget variance commentary drafting for FP&A through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into manual commentary writing for monthly reviews.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to analyze actuals vs budget and prepare draft variance explanations. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "scenario-planning-assistant",
    subIndustry: "FP&A",
    title: "Scenario Planning Assistant",
    seoKeywords:
      "scenario planning assistant automation, fp&a AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "FP&A teams often manage scenario planning assistant for FP&A through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into manual scenario modelling and slow business inputs.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to build scenario assumptions and summarize financial impact for review. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // General Ledger Close
  {
    id: "journal-entry-validation",
    subIndustry: "General Ledger Close",
    title: "Journal Entry Validation",
    seoKeywords:
      "journal entry validation automation, general ledger close AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "GL Close teams often manage journal entry validation for General Ledger Close through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into manual journal checks and weak supporting evidence.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate journal type, limits, attachments and approval routing. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "accrual-estimation-support",
    subIndustry: "General Ledger Close",
    title: "Accrual Estimation Support",
    seoKeywords:
      "accrual estimation support automation, general ledger close AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "GL Close teams often manage accrual estimation support for General Ledger Close through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into accruals based on scattered inputs and late approvals.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to analyze open POs, GRNs and historical trends to suggest accruals. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Expense Management
  {
    id: "expense-policy-validation",
    subIndustry: "Expense Management",
    title: "Expense Policy Validation",
    seoKeywords:
      "expense policy validation automation, expense management AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Expense Management teams often manage expense policy validation for Expense Management through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into claims approved without consistent policy checks.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to compare claims with policy, limits and travel rules. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Fixed Assets
  {
    id: "asset-capitalization-workflow",
    subIndustry: "Fixed Assets",
    title: "Asset Capitalization Workflow",
    seoKeywords:
      "asset capitalization workflow automation, fixed assets AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Fixed Assets teams often manage asset capitalization workflow for Fixed Assets through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into assets capitalized late or with missing documents.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate invoice, GRN and asset category before capitalization. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Procurement Finance
  {
    id: "invoice-hold-root-cause-analysis",
    subIndustry: "Procurement Finance",
    title: "Invoice Hold Root Cause Analysis",
    seoKeywords:
      "invoice hold root cause analysis automation, procurement finance AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Procurement Finance teams often manage invoice hold root cause analysis for Procurement Finance through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into invoice holds resolved slowly.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to summarize hold reasons and recommend responsible action owner. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Internal Audit
  {
    id: "audit-evidence-collection",
    subIndustry: "Internal Audit",
    title: "Audit Evidence Collection",
    seoKeywords:
      "audit evidence collection automation, audit AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Internal Audit teams often manage audit evidence collection through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility as auditors chase evidence across emails and shared folders.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to request, collect, index and track audit evidence for audit preparation. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "policy-compliance-review-assistant",
    subIndustry: "Internal Audit",
    title: "Policy Compliance Review Assistant",
    seoKeywords:
      "policy compliance review assistant automation, audit AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Internal Audit teams often manage policy compliance review assistant for audits through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into manual review of transactions against policy rules.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to compare transactions with policy rules and summarize exceptions. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Finance Compliance Reporting
  {
    id: "delegation-of-authority-check",
    subIndustry: "Finance Compliance Reporting",
    title: "Delegation of Authority Check",
    seoKeywords:
      "delegation of authority check automation, finance compliance reporting AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Finance Compliance teams often manage delegation of authority check for Finance Compliance Reporting through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into approvals checked manually against authority matrix.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to validate transaction values and approvers against DOA rules. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "policy-query-assistant",
    subIndustry: "Finance Compliance Reporting",
    title: "Policy Query Assistant",
    seoKeywords:
      "policy query assistant automation, finance compliance reporting AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Finance Compliance teams often manage policy query assistant for Finance Compliance Reporting through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into finance teams repeatedly ask policy questions.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to answer approved policy questions and route exceptions to finance owners. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Finance Shared Services
  {
    id: "finance-email-triage",
    subIndustry: "Finance Shared Services",
    title: "Finance Email Triage",
    seoKeywords:
      "finance email triage automation, finance shared services AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Finance Shared Services teams often manage finance email triage for Finance Shared Services through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into mixed finance requests in one inbox.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to classify emails, extract intent and create tickets or draft replies. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "finance-knowledge-assistant",
    subIndustry: "Finance Shared Services",
    title: "Finance Knowledge Assistant",
    seoKeywords:
      "finance knowledge assistant automation, finance shared services AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Finance Shared Services teams often manage finance knowledge assistant for Finance Shared Services through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into repeated searches for SOPs and approved finance policies.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to answer SOP questions using approved knowledge base and citations. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  {
    id: "exception-queue-prioritization",
    subIndustry: "Finance Shared Services",
    title: "Exception Queue Prioritization",
    seoKeywords:
      "exception queue prioritization automation, finance shared services AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Finance Shared Services teams often manage exception queue prioritization for Finance Shared Services through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into work queues handled first-in-first-out without risk context.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to prioritize exceptions based on amount, age, SLA and business impact. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
  // Payroll Finance
  {
    id: "statutory-deduction-check",
    subIndustry: "Payroll Finance",
    title: "Statutory Deduction Check",
    seoKeywords:
      "statutory deduction check automation, payroll finance AI automation, financial domain RPA services, agentic AI solution",
    challenge:
      "Payroll Finance teams often manage statutory deduction check for Payroll Finance through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into payroll deductions validated manually against country rules.",
    solution:
      "Deploy an Agentic AI + Workflow Automation solution to compare payroll deductions with country rules and employee master data. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
  },
];

export const FINANCIAL_USE_CASES = RAW.map(withImage);

const GROUP_ORDER = [
  "Accounts Payable",
  "Procure to Pay",
  "Accounts Receivable",
  "Order to Cash",
  "Treasury",
  "Tax",
  "FP&A",
  "General Ledger Close",
  "Expense Management",
  "Fixed Assets",
  "Procurement Finance",
  "Internal Audit",
  "Finance Compliance Reporting",
  "Finance Shared Services",
  "Payroll Finance",
];

export const FINANCIAL_GROUPS = GROUP_ORDER.map((label) => ({
  id: slug(label),
  label,
})).filter((g) => FINANCIAL_USE_CASES.some((uc) => uc.groupId === g.id));

export const FINANCE_PROCESS_TOWERS = FINANCIAL_GROUPS;

export const USE_CASES_BY_TOWER = FINANCIAL_GROUPS.reduce((acc, group) => {
  acc[group.id] = FINANCIAL_USE_CASES.filter((uc) => uc.groupId === group.id);
  return acc;
}, {});

// ===========================================================================
// SEO + LLM-FRIENDLY ENRICHMENT (additive only — nothing above was modified)
// ===========================================================================
//
// What this adds and why:
// 1. keywordsArray      — machine-readable keywords (schema.org "keywords"
//                          wants an array/CSV string; search engines and LLM
//                          crawlers parse arrays more reliably than prose).
// 2. metaTitle / metaDescription — pre-trimmed to safe SEO lengths (<=60 /
//                          <=155 chars) for <title> and <meta name="description">.
// 3. summary             — one-sentence, plain-language answer LLMs (AI
//                          Overviews, ChatGPT, Perplexity) can lift directly
//                          as a citeable snippet.
// 4. faqs                — Q&A pairs generated from your existing challenge/
//                          solution text. Q&A formatting is what answer
//                          engines and FAQPage rich results key off of.
// 5. canonicalPath        — a stable URL path for canonical tags + JSON-LD
//                          "url" fields.
// 6. JSON-LD builders     — generateUseCaseJsonLd, buildItemListJsonLd,
//                          buildFaqPageJsonLd, buildBreadcrumbJsonLd. Render
//                          these into <script type="application/ld+json">
//                          tags in your page <head> / page body.
// ---------------------------------------------------------------------------

const truncate = (str, max) =>
  str.length <= max ? str : `${str.slice(0, max - 1).trimEnd()}…`;

function enrichForSeoAndLlm(uc) {
  const keywordsArray = uc.seoKeywords.split(",").map((k) => k.trim());
  const canonicalPath = `/agentic-ai/financial-domain/${uc.groupId}/${uc.id}`;
  const metaTitle = truncate(
    `${uc.title} | ${uc.subIndustry} Agentic AI Automation`,
    60
  );
  const metaDescription = truncate(uc.solution, 155);
  const summary = `${uc.title} is an Agentic AI + Workflow Automation use case for ${uc.subIndustry} that ${uc.solution
    .charAt(0)
    .toLowerCase()}${uc.solution.slice(1)}`;

  const faqs = [
    {
      question: `What problem does ${uc.title} solve in ${uc.subIndustry}?`,
      answer: uc.challenge,
    },
    {
      question: `How does Agentic AI automate ${uc.title.toLowerCase()}?`,
      answer: uc.solution,
    },
  ];

  return {
    ...uc,
    keywordsArray,
    canonicalPath,
    canonicalUrl: `${SITE_BASE_URL}${canonicalPath}`,
    metaTitle,
    metaDescription,
    summary,
    faqs,
  };
}

// Same order/content as FINANCIAL_USE_CASES, with SEO/LLM fields layered on.
export const FINANCIAL_USE_CASES_SEO = FINANCIAL_USE_CASES.map(enrichForSeoAndLlm);
export const FINANCIAL_DOMAIN_FAQS = buildDomainFaqs(
  FINANCIAL_DOMAIN_INTRO,
  FINANCIAL_USE_CASES_SEO
);

export const USE_CASES_BY_TOWER_SEO = FINANCIAL_GROUPS.reduce((acc, group) => {
  acc[group.id] = FINANCIAL_USE_CASES_SEO.filter((uc) => uc.groupId === group.id);
  return acc;
}, {});

/**
 * JSON-LD "Service" schema for a single use case.
 * Render with: <script type="application/ld+json">{JSON.stringify(generateUseCaseJsonLd(uc))}</script>
 */
export function generateUseCaseJsonLd(uc) {
  const enriched = uc.faqs ? uc : enrichForSeoAndLlm(uc);
  const image = absoluteAssetUrl(enriched.image);
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${enriched.canonicalUrl}#service`,
        name: enriched.title,
        description: enriched.solution,
        serviceType: `${enriched.subIndustry} Agentic AI Automation`,
        areaServed: ["IN", "AE", "SA", "GB", "US"],
        url: enriched.canonicalUrl,
        image,
        provider: ORG,
        keywords: enriched.keywordsArray.join(", "),
      },
      {
        "@type": "TechArticle",
        "@id": `${enriched.canonicalUrl}#article`,
        headline: enriched.title,
        description: enriched.metaDescription,
        articleBody: `${enriched.challenge} ${enriched.solution}`,
        keywords: enriched.keywordsArray.join(", "),
        url: enriched.canonicalUrl,
        image,
        author: ORG,
        publisher: ORG,
        mainEntityOfPage: enriched.canonicalUrl,
        about: {
          "@type": "Thing",
          name: `${enriched.subIndustry} Agentic AI Automation`,
        },
      },
    ],
  };
}

/**
 * FAQPage JSON-LD for a single use case (good for rich results + LLM answer
 * engines). Render on the use-case detail page.
 */
export function generateUseCaseFaqJsonLd(uc) {
  const enriched = uc.faqs ? uc : enrichForSeoAndLlm(uc);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: enriched.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}

/**
 * ItemList JSON-LD for a listing page (e.g. /financial-domain or a
 * per-group page). Pass a filtered array (e.g. USE_CASES_BY_TOWER_SEO[groupId])
 * or FINANCIAL_USE_CASES_SEO for the full catalog.
 */
export function buildItemListJsonLd(useCases, listName = FINANCIAL_DOMAIN_INTRO.title) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    itemListElement: useCases.map((uc, i) => {
      const enriched = uc.faqs ? uc : enrichForSeoAndLlm(uc);
      return {
        "@type": "ListItem",
        position: i + 1,
        name: enriched.title,
        url: enriched.canonicalUrl,
      };
    }),
  };
}

/**
 * BreadcrumbList JSON-LD for a use-case detail page.
 */
export function buildBreadcrumbJsonLd(uc) {
  const enriched = uc.faqs ? uc : enrichForSeoAndLlm(uc);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: FINANCIAL_DOMAIN_INTRO.title,
        item: `${SITE_BASE_URL}/agentic-ai/financial-domain`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: enriched.subIndustry,
        item: `${SITE_BASE_URL}/agentic-ai/financial-domain/${enriched.groupId}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: enriched.title,
        item: enriched.canonicalUrl,
      },
    ],
  };
}

/**
 * Aggregated FAQPage JSON-LD across every use case — useful for a single
 * "Financial Domain FAQ" page that LLM answer engines can crawl in one pass.
 */
export const FINANCIAL_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FINANCIAL_USE_CASES_SEO.flatMap((uc) =>
    uc.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    }))
  ),
};

/**
 * Full-catalog ItemList JSON-LD, precomputed for convenience.
 */
export const FINANCIAL_ITEM_LIST_JSONLD = buildItemListJsonLd(FINANCIAL_USE_CASES_SEO);

export const FINANCIAL_DOMAIN_BREADCRUMB_JSONLD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE_URL },
    {
      "@type": "ListItem",
      position: 2,
      name: "Agentic AI",
      item: `${SITE_BASE_URL}/agentic-ai`,
    },
    {
      "@type": "ListItem",
      position: 3,
      name: FINANCIAL_DOMAIN_INTRO.title,
      item: `${SITE_BASE_URL}/agentic-ai/financial-domain`,
    },
  ],
};

export const FINANCIAL_WEBPAGE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: `${FINANCIAL_DOMAIN_INTRO.title} Agentic AI Use Cases`,
  description: FINANCIAL_DOMAIN_INTRO.seoDescription,
  url: `${SITE_BASE_URL}/agentic-ai/financial-domain`,
  inLanguage: "en",
  isPartOf: {
    "@type": "WebSite",
    name: "Surens Inffotek",
    url: SITE_BASE_URL,
  },
  publisher: ORG,
  about: {
    "@type": "Thing",
    name: `${FINANCIAL_DOMAIN_INTRO.title} Agentic AI Automation`,
    description: FINANCIAL_DOMAIN_INTRO.content,
  },
  mainEntity: FINANCIAL_ITEM_LIST_JSONLD,
};
